import { useState } from 'react';
import axios from 'axios';
import { Trash2, AlertTriangle, Loader2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Two-step bulk-cleanup of test users:
 *   1) Preview — shows count + sample + cascade impact
 *   2) Execute — actually deletes, requires typing "DELETE" to confirm.
 *
 * Patterns matched (server-side, see /app/backend/routers/admin_cleanup.py):
 *   - emails like @test., @example., test@, +test@, @mailinator, @tempmail
 *   - explicit legacy seeds: cleaner@test.com, mike@cleaner.com, customer@test.com
 *   - names starting with "Test ", "Test_", "QA ", "Demo "
 * Admins are NEVER deleted.
 */
const TestDataCleanupModal = ({ open, onClose, onDone }) => {
  const [whitelist, setWhitelist] = useState('');
  const [extra, setExtra] = useState('');
  const [includePatterns, setIncludePatterns] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const payload = () => ({
    whitelist_emails: whitelist.split(/[\n,]/).map(s => s.trim()).filter(Boolean),
    extra_emails: extra.split(/[\n,]/).map(s => s.trim()).filter(Boolean),
    include_pattern_match: includePatterns,
  });

  const runPreview = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/cleanup/test-users/preview`, payload(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const runExecute = async () => {
    if (confirmText !== 'DELETE') {
      setError('Type DELETE in capitals to confirm.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/cleanup/test-users/execute`, payload(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(res.data);
      setPreview(null);
      setConfirmText('');
      if (onDone) onDone();
    } catch (e) {
      setError(e.response?.data?.detail || 'Cleanup failed');
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setPreview(null);
    setResult(null);
    setError(null);
    setConfirmText('');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      data-testid="cleanup-modal-backdrop"
      onClick={close}
    >
      <div
        className="bg-surface rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="cleanup-modal"
      >
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-red-50">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-semibold text-text-primary">Remove test users</h2>
              <p className="text-xs text-text-secondary">Cascade-deletes accounts, bookings, places, reviews & more.</p>
            </div>
          </div>
          <button onClick={close} className="text-text-secondary hover:text-text-primary" data-testid="cleanup-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Pattern toggle */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={includePatterns}
              onChange={(e) => setIncludePatterns(e.target.checked)}
              className="mt-1"
              data-testid="cleanup-include-patterns"
            />
            <div>
              <p className="text-sm font-medium text-text-primary">Auto-detect test emails & names</p>
              <p className="text-xs text-text-secondary">
                Matches @test., @example., test@, +test@, @mailinator, @tempmail, legacy seeds,
                and names starting with &quot;Test &quot;, &quot;QA &quot;, &quot;Demo &quot;. Admins are always preserved.
              </p>
            </div>
          </label>

          {/* Whitelist */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Keep these emails (one per line or comma-separated)
            </label>
            <textarea
              value={whitelist}
              onChange={(e) => setWhitelist(e.target.value)}
              rows={2}
              placeholder="real-customer@gmail.com, real-cleaner@yahoo.com"
              className="input-field w-full font-mono text-xs"
              data-testid="cleanup-whitelist"
            />
          </div>

          {/* Extra to delete */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Also delete these specific emails (optional)
            </label>
            <textarea
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              rows={2}
              placeholder="john.doe@example.com"
              className="input-field w-full font-mono text-xs"
              data-testid="cleanup-extra"
            />
          </div>

          {/* Action: preview */}
          {!preview && !result && (
            <button
              onClick={runPreview}
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="cleanup-preview-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Preview what will be deleted
            </button>
          )}

          {/* Preview results */}
          {preview && !result && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3" data-testid="cleanup-preview-result">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900">
                    {preview.user_count} users + {preview.total_records - preview.user_count} related records will be deleted
                  </p>
                  <p className="text-xs text-amber-800 mt-1">This action is irreversible. Review the cascade below.</p>
                </div>
              </div>

              {/* Cascade counts */}
              {preview.cascade_counts && Object.keys(preview.cascade_counts).length > 0 && (
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(preview.cascade_counts).map(([k, v]) => (
                    <div key={k} className="bg-white/60 rounded-lg px-3 py-2 flex items-center justify-between">
                      <span className="text-text-secondary">{k.replace(/_/g, ' ')}</span>
                      <span className="font-semibold text-text-primary">{v}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Sample users */}
              {preview.users_sample && preview.users_sample.length > 0 && (
                <div className="bg-white/60 rounded-lg p-3 max-h-48 overflow-y-auto" data-testid="cleanup-sample">
                  <p className="text-xs font-semibold text-text-primary mb-2">
                    Sample ({preview.users_sample.length} of {preview.user_count}):
                  </p>
                  <ul className="space-y-1 text-xs font-mono">
                    {preview.users_sample.map(u => (
                      <li key={u.id} className="text-text-secondary">
                        {u.email} <span className="opacity-60">— {u.name} ({u.role})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {preview.user_count > 0 && (
                <div className="pt-2 border-t border-amber-200 space-y-2">
                  <label className="block text-xs font-medium text-amber-900">
                    Type <span className="font-mono font-bold">DELETE</span> to confirm:
                  </label>
                  <input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="DELETE"
                    className="input-field w-full font-mono"
                    data-testid="cleanup-confirm-input"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setPreview(null); setConfirmText(''); }}
                      className="flex-1 py-2 px-4 rounded-full border border-border text-text-secondary hover:bg-surface-muted text-sm font-medium"
                      data-testid="cleanup-cancel"
                    >
                      Back
                    </button>
                    <button
                      onClick={runExecute}
                      disabled={loading || confirmText !== 'DELETE'}
                      className="flex-1 py-2 px-4 rounded-full bg-red-600 text-white hover:bg-red-700 disabled:bg-red-300 text-sm font-medium flex items-center justify-center gap-2"
                      data-testid="cleanup-execute-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      Delete {preview.user_count} users
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Final result */}
          {result && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4" data-testid="cleanup-result">
              <p className="text-sm font-semibold text-green-900 mb-2">{result.message}</p>
              {result.deleted && (
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(result.deleted).map(([k, v]) => (
                    <div key={k} className="bg-white/60 rounded-lg px-3 py-1.5 flex items-center justify-between">
                      <span className="text-green-800">{k.replace(/_/g, ' ')}</span>
                      <span className="font-semibold text-green-900">{v}</span>
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={close}
                className="btn-primary mt-4 w-full"
                data-testid="cleanup-done"
              >
                Done
              </button>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800" data-testid="cleanup-error">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestDataCleanupModal;
